/**
 * THE TOUR REGISTRY.
 *
 * Authoring a new tour is: write a data file next to this one, import it, add it to the array. That
 * is the whole contract, and it is the reason the owner's future sales-demo tour is a data file and
 * not a second system — it will differ from an onboarding tour only in `audience: AUDIENCE.DEMO`,
 * shorter copy, and which anchors it visits.
 *
 * Order matters: it is the order the launcher lists them in, so put the tour a brand-new user needs
 * first.
 *
 * Every tour here is (a) schema-validated at import by defineTour, and (b) anchor-checked against
 * the real components by ../__tests__/tourAnchors.contract.test.jsx, which mounts the screen each
 * step lives on and fails CI by name if the anchor has stopped existing. Adding a tour without
 * adding its screen to that test's harness table is itself a test failure — there is no way to
 * register a tour that nothing checks.
 */
import firstDailyReport from './daily-report'
import walkTheFinishedWorkStep from './finished-work'
import crewTimeSignoff from './crew-time'
import issueFieldPin from './field-pin'
import equipmentSticker from './equipment-sticker'
import jsaFill from './jsa-fill'
import capexEstimate from './capex-estimate'

// `defineTour` returns null instead of throwing in a PRODUCTION build (see tourSchema.js), so a
// malformed tour drops out of the menu rather than white-screening the app on import.
export const TOURS = [
  // Order is deliberate and it is the workflow's order: you cannot sign off hours that nobody has
  // written down yet. crew-time picks up exactly where the daily report leaves off.
  firstDailyReport,
  // Straight after the first report, for a foreman whose job was priced in Project Financials (closed loop
  // Phase 3): the one step that report grows there. Offered only where Project Financials is on.
  walkTheFinishedWorkStep,
  crewTimeSignoff,
  // Then a different job for a different desk: the two above are the field supervisor's week, this
  // one is the office setting a field hand up in the first place. It is also the only tour here that
  // never appears for a foreman — see its header for why.
  issueFieldPin,
  // Last, and the other half of the one above. A field hand with a PIN still needs something to scan;
  // a sticker on a machine still needs somebody holding a PIN to sign at it. This is the equipment
  // side of that pair, and the only tour here written for whoever manages machines rather than people.
  equipmentSticker,
  // The safety sheet a crew fills at the job box. It sits AFTER the sticker tour because the two are
  // the same question answered twice and the contrast is the lesson: the equipment sheet is the one
  // you can scan a code to fill, and this one is the one you cannot — it asks for a real job and real
  // people, so it can only be filled by somebody logged in. Reading them in this order is what makes
  // "why isn't the JSA on a sticker" answer itself.
  jsaFill,
  // A different desk again: the office pricing a capital job, in Project Financials. Last because it
  // is the longest and the only one that walks a whole record's life — estimate to award to project —
  // and because nobody in the field is ever offered it (it stands on View Bids, Project Financials and
  // Capital Projects being on). It walks a practice estimate that exists only for the length of the run.
  capexEstimate,
].filter(Boolean)

export default TOURS
